/**
 * Onboarding Wizard 2.0 — The complete connector onboarding experience.
 *
 * PHILOSOPHY:
 *   Step 0 = Sign up for a free RetailsyncGo account → get your subdomain.
 *   Without a subdomain, there's no storefront. This is the foundation.
 *
 * FLOW:
 *   0. Prerequisite: Sign up at app.retailsync.africa (free subdomain)
 *   1. Choose Goal: Migrate | Connect API | Sell Online
 *   2. Select Entity: Products | Inventory | Customers | Transactions
 *   3. Choose Source: File Upload | Marketplace Provider | Manual
 *   4. Map Fields: Smart auto-detection with review
 *   5. Preview & Import: See data before syncing
 *   6. Done: Summary + storefront link + next steps
 */

import { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retailsync/ui'
import { Button } from '@retailsync/ui'
import {
  ArrowUpTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  CloudArrowUpIcon,
  ShoppingBagIcon,
  PuzzlePieceIcon,
  DocumentTextIcon,
  CpuChipIcon,
  EyeIcon,
  ArrowRightIcon,
  UserPlusIcon,
  ArrowTopRightOnSquareIcon,
  Squares2X2Icon,
  UserGroupIcon,
  TableCellsIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import {
  autoMapFields,
  generateSummaryText,
  getFieldByEntity,
  getFieldByKey,
  type AutoMapping,
  type MappingReport,
} from '../services/smart-field-mapper'
import { parseDataFile, buildSampleValues } from '../services/file-parser'
import { API_ORIGIN } from '../config/api'

// ─── Types ──────────────────────────────────────────────────────────────────

type Goal = 'migrate' | 'connect-api' | 'sell-online' | null
type Entity = 'products' | 'customers' | 'inventory' | 'transactions'
type Source = 'file' | 'marketplace' | 'manual'
type Step = 'prerequisite' | 'goal' | 'entity' | 'source' | 'map' | 'preview' | 'import' | 'done'

interface FileData {
  headers: string[]
  rows: Record<string, any>[]
  fileName: string
  totalRows: number
}

interface SyncResult {
  received: number
  created: number
  updated: number
  skipped: number
  errors: string[]
}

const API_BASE = API_ORIGIN

const GOALS = [
  {
    key: 'migrate' as Goal,
    icon: ArrowUpTrayIcon,
    title: "I'm migrating from another POS",
    description: 'Import your existing products, inventory, customers, and sales history. We auto-detect your file format.',
    color: 'blue',
  },
  {
    key: 'connect-api' as Goal,
    icon: PuzzlePieceIcon,
    title: 'I want to connect an API provider',
    description: 'Link Square, Shopify, WooCommerce, or any REST API. Real-time sync, webhooks, and order bridging.',
    color: 'purple',
  },
  {
    key: 'sell-online' as Goal,
    icon: ShoppingBagIcon,
    title: 'I just want to start selling online',
    description: 'Quick product setup → get your storefront link → start taking orders. Fastest path to live.',
    color: 'green',
  },
]

const ENTITIES: { key: Entity; label: string; icon: any; desc: string }[] = [
  { key: 'products', label: 'Products', icon: Squares2X2Icon, desc: 'Your catalog — names, prices, SKUs, categories' },
  { key: 'inventory', label: 'Inventory', icon: TableCellsIcon, desc: 'Stock levels across branches and warehouses' },
  { key: 'customers', label: 'Customers', icon: UserGroupIcon, desc: 'Customer profiles, contact details, types' },
  { key: 'transactions', label: 'Transactions', icon: BanknotesIcon, desc: 'Sales history, payments, order records' },
]

const SOURCES: { key: Source; label: string; icon: any; desc: string }[] = [
  { key: 'file', label: 'Upload a File', icon: DocumentTextIcon, desc: 'CSV or Excel export from your current POS' },
  { key: 'marketplace', label: 'Marketplace Provider', icon: PuzzlePieceIcon, desc: 'Connect Square, Shopify, WooCommerce, etc.' },
  { key: 'manual', label: 'Manual Entry', icon: CpuChipIcon, desc: 'Type or paste your data — great for small catalogs' },
]

// ─── Component ──────────────────────────────────────────────────────────────

export function OnboardingWizardV2() {
  const { tenant, isAuthenticated } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Wizard state
  const [step, setStep] = useState<Step>('prerequisite')
  const [goal, setGoal] = useState<Goal>(null)
  const [entity, setEntity] = useState<Entity>('products')
  const [, setSource] = useState<Source>('file')

  // Data state
  const [fileData, setFileData] = useState<FileData | null>(null)
  const [mappingReport, setMappingReport] = useState<MappingReport | null>(null)
  const [manualOverrides, setManualOverrides] = useState<Record<string, string>>({})
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // ── If already authenticated, skip prerequisite ──────────────────────────

  useEffect(() => {
    if (isAuthenticated && step === 'prerequisite') {
      setStep('goal')
    }
  }, [isAuthenticated, step])

  // ── Navigation ────────────────────────────────────────────────────────────

  const STEPS: Step[] = ['prerequisite', 'goal', 'entity', 'source', 'map', 'preview', 'import', 'done']
  const currentIdx = STEPS.indexOf(step)

  const goNext = () => {
    const next = STEPS[currentIdx + 1]
    if (next) setStep(next)
  }
  const goBack = () => {
    const prev = STEPS[currentIdx - 1]
    if (prev) setStep(prev)
  }

  // ── File Parsing ──────────────────────────────────────────────────────────

  const handleFile = async (file: File) => {
    setLoading(true)
    setError(null)
    try {
      // CSV / XLSX / XLS / JSON — all parsed by the shared connector parser.
      const parsed = await parseDataFile(file)
      const headers = parsed.headers
      const dataRows = parsed.rows
      const fd: FileData = {
        headers,
        rows: dataRows,
        fileName: parsed.fileName,
        totalRows: parsed.totalRows,
      }
      setFileData(fd)

      const report = autoMapFields(entity, headers, buildSampleValues(dataRows, headers))
      setMappingReport(report)

      const overrides: Record<string, string> = {}
      report.detected.forEach(d => { overrides[d.sourceHeader] = d.targetField })
      report.unmapped.forEach(u => { overrides[u] = '' })
      setManualOverrides(overrides)

      setStep('map')
    } catch (err: any) { setError(err.message) }
    finally { setLoading(false) }
  }

  // ── Sync ──────────────────────────────────────────────────────────────────

  const getMappingsForSync = (): AutoMapping[] => {
    return Object.entries(manualOverrides)
      .filter(([_, target]) => !!target)
      .map(([source, target]) => ({
        sourceHeader: source,
        targetField: target,
        confidence: mappingReport?.detected.find(d => d.sourceHeader === source)?.confidence || 0,
        method: 'exact' as const,
      }))
  }

  const handleSync = async () => {
    if (!fileData) return
    setStep('import')
    setError(null)

    const mappings = getMappingsForSync()
    const mappedRows = fileData.rows.map(row => {
      const mapped: Record<string, any> = {}
      for (const m of mappings) mapped[m.targetField] = row[m.sourceHeader]
      return mapped
    })

    const token = localStorage.getItem('access_token') || ''
    const tenantId = tenant?.id || localStorage.getItem('tenant_id') || ''

    const epMap: Record<Entity, string> = {
      products: '/products/bulk',
      customers: '/customers/bulk',
      inventory: '/inventory/bulk',
      transactions: '/transactions/bulk',
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1${epMap[entity]}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
        body: JSON.stringify({ items: mappedRows }),
      })
      const json = await res.json()
      if (json.success) {
        setSyncResult({
          received: json.received || fileData.totalRows,
          created: json.created || 0,
          updated: json.updated || 0,
          skipped: (json.received || fileData.totalRows) - ((json.created || 0) + (json.updated || 0)),
          errors: json.errors || [],
        })
        setStep('done')
      } else {
        setError(json.error || 'Sync failed')
        setStep('map')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
      setStep('map')
    }
  }

  // ── Reset ─────────────────────────────────────────────────────────────────

  const reset = () => {
    setStep('goal'); setGoal(null); setEntity('products'); setSource('file')
    setFileData(null); setMappingReport(null); setManualOverrides({})
    setSyncResult(null); setError(null)
  }

  // ─── Step Indicator ───────────────────────────────────────────────────────

  const visibleSteps: Step[] = ['prerequisite', 'goal', 'entity', 'source', 'map', 'preview']
  const labels: Record<string, string> = {
    prerequisite: 'Account',
    goal: 'Goal',
    entity: 'Entity',
    source: 'Source',
    map: 'Map',
    preview: 'Preview',
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-full">
      {/* Top progress bar */}
      <div className="bg-white border border-gray-200 rounded-lg px-3 sm:px-4 py-3 mb-6">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          {visibleSteps.map((s, i) => {
            const si = STEPS.indexOf(s)
            return (
              <div key={s} className="flex items-center gap-2">
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  currentIdx >= si ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                }`}>
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                    currentIdx > si ? 'bg-blue-600 text-white' :
                    currentIdx === si ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
                  }`}>
                    {currentIdx > si ? '✓' : i + 1}
                  </span>
                  {labels[s]}
                </div>
                {i < visibleSteps.length - 1 && <div className="w-4 h-px bg-gray-300 hidden sm:block" />}
              </div>
            )
          })}
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-2 sm:py-4">
        {/* ═══ STEP 0: PREREQUISITE ═══ */}
        {step === 'prerequisite' && (
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
              <UserPlusIcon className="w-4 h-4" />
              Step 1: Get Your Free Account
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome to RetailsyncGo</h1>
            <p className="text-gray-500 max-w-lg mx-auto">
              Every RetailsyncGo store runs on its own subdomain — like <strong>mystore.retailsync.africa</strong>.
              Sign up for free and get yours in under 60 seconds.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              {[
                { icon: UserPlusIcon, title: '1. Sign Up', desc: 'Create your free account at app.retailsync.africa' },
                { icon: GlobeAltIcon, title: '2. Get Subdomain', desc: 'Your store gets mystore.retailsync.africa — free' },
                { icon: ArrowRightIcon, title: '3. Come Back Here', desc: 'Sign in to this connector and start importing' },
              ].map(item => (
                <Card key={item.title}>
                  <CardContent className="pt-5 text-center">
                    <item.icon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <h3 className="font-semibold text-sm text-gray-900">{item.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex flex-col items-center gap-3">
              <a
                href="https://app.retailsync.africa/signup"
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
              >
                Sign Up Free at app.retailsync.africa
                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
              </a>
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <a href="https://app.retailsync.africa/login" target="_blank" rel="noopener" className="text-blue-600 hover:underline">
                  Sign in here
                </a>
                {' '}then come back and continue.
              </p>
              <Button variant="ghost" onClick={() => setStep('goal')} className="text-sm">
                I already signed up — continue →
              </Button>
            </div>

            {/* What you get */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="pt-5">
                <h3 className="font-semibold text-blue-900 mb-3">What your free account includes:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                  {[
                    'Free subdomain (mystore.retailsync.africa)',
                    'Online storefront with cart & checkout',
                    'Product & inventory management',
                    'Customer ledger & loyalty',
                    '1 branch, 2 POS terminals',
                    'Email support',
                  ].map(f => (
                    <div key={f} className="flex items-center gap-1.5">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ STEP 1: GOAL ═══ */}
        {step === 'goal' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">What brings you here?</h2>
              <p className="text-gray-500 mt-1">Choose your path — we'll guide you through it.</p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {GOALS.map(g => (
                <button
                  key={g.key}
                  onClick={() => { setGoal(g.key); goNext() }}
                  className={`text-left p-6 rounded-xl border-2 transition-all hover:shadow-md ${
                    `border-${g.color}-200 hover:border-${g.color}-400 bg-white`
                  }`}
                  style={{ borderColor: g.key === 'migrate' ? '#93c5fd' : g.key === 'connect-api' ? '#c4b5fd' : '#86efac' }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      g.color === 'blue' ? 'bg-blue-100' : g.color === 'purple' ? 'bg-purple-100' : 'bg-green-100'
                    }`}>
                      <g.icon className={`w-6 h-6 ${
                        g.color === 'blue' ? 'text-blue-600' : g.color === 'purple' ? 'text-purple-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg">{g.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{g.description}</p>
                    </div>
                    <ArrowRightIcon className="w-5 h-5 text-gray-400 mt-3" />
                  </div>
                </button>
              ))}
            </div>

            <div className="text-center">
              <Button variant="ghost" onClick={() => setStep('prerequisite')}>
                ← Back: I need to sign up first
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: ENTITY ═══ */}
        {step === 'entity' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {goal === 'migrate' ? 'What do you want to import first?' :
                 goal === 'connect-api' ? 'What data do you want to sync?' :
                 'What do you want to set up?'}
              </h2>
              <p className="text-gray-500 mt-1">You can do more later — start with the most important one.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {ENTITIES.map(e => (
                <button
                  key={e.key}
                  onClick={() => { setEntity(e.key); goNext() }}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    entity === e.key
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <e.icon className={`w-6 h-6 ${entity === e.key ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div>
                      <h3 className="font-semibold text-gray-900">{e.label}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{e.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={goBack}>← Back</Button>
              <Button onClick={goNext} className="flex items-center gap-2">
                Continue <ArrowRightIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: SOURCE ═══ */}
        {step === 'source' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Where is your data?</h2>
              <p className="text-gray-500 mt-1">
                Importing <strong>{ENTITIES.find(e => e.key === entity)?.label}</strong>
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {SOURCES.map(s => (
                <button
                  key={s.key}
                  onClick={() => {
                    setSource(s.key)
                    if (s.key === 'file') {
                      fileInputRef.current?.click()
                    } else if (s.key === 'marketplace') {
                      window.location.hash = '#/marketplace'
                    }
                  }}
                  className="text-left p-5 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-300 transition-all flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-6 h-6 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{s.label}</h3>
                    <p className="text-sm text-gray-500">{s.desc}</p>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.xlsm,.json"
              className="hidden"
              onChange={e => { const file = e.target.files?.[0]; if (file) handleFile(file) }}
            />

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            {loading && (
              <div className="flex items-center justify-center gap-2 text-gray-500 py-4">
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                Parsing your file...
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={goBack}>← Back</Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 4: MAP FIELDS ═══ */}
        {step === 'map' && fileData && mappingReport && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Review Field Mappings</h2>
              <p className="text-gray-500 mt-1">
                We auto-detected {mappingReport.detected.length} of {fileData.headers.length} columns.
                {mappingReport.missing.length > 0 && (
                  <span className="text-amber-600"> {mappingReport.missing.length} required field(s) need attention.</span>
                )}
              </p>
            </div>

            <Card>
              <CardContent className="pt-5 p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-2 px-4 font-medium text-gray-500">Your Column</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-500">Maps To</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-500">Confidence</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-500 hidden md:table-cell">Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fileData.headers.map(header => {
                        const detected = mappingReport.detected.find(d => d.sourceHeader === header)
                        const fields = getFieldByEntity(entity)
                        const samples = fileData.rows.slice(0, 2).map(r => String(r[header] || '')).join(', ')

                        return (
                          <tr key={header} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-4 font-medium text-gray-900">{header}</td>
                            <td className="py-2 px-4">
                              <select
                                value={manualOverrides[header] || ''}
                                onChange={e => setManualOverrides(prev => ({ ...prev, [header]: e.target.value }))}
                                className={`w-full px-2 py-1 border rounded text-sm ${
                                  manualOverrides[header] ? 'border-blue-300 bg-blue-50' : 'border-red-300 bg-red-50'
                                }`}
                              >
                                <option value="">— Skip —</option>
                                {fields.map(f => (
                                  <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>
                                ))}
                              </select>
                            </td>
                            <td className="py-2 px-4">
                              {detected ? (
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  detected.confidence >= 90 ? 'bg-green-100 text-green-800' :
                                  detected.confidence >= 70 ? 'bg-amber-100 text-amber-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {detected.confidence}% {detected.method}
                                </span>
                              ) : (
                                <span className="text-xs text-red-600">Unmapped</span>
                              )}
                            </td>
                            <td className="py-2 px-4 text-xs text-gray-400 max-w-[180px] truncate hidden md:table-cell" title={samples}>
                              {samples}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {mappingReport.missing.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Missing required fields:</strong> {mappingReport.missing.map(f => f.label).join(', ')}.
                Add these columns to your file or create manual mappings above.
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => { setFileData(null); setStep('source') }}>
                ← Choose Different File
              </Button>
              <Button
                onClick={() => setStep('preview')}
                disabled={mappingReport.missing.length > 0}
                className="flex items-center gap-2"
              >
                <EyeIcon className="w-4 h-4" />
                Preview Mapped Data
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 5: PREVIEW ═══ */}
        {step === 'preview' && fileData && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Preview Your Data</h2>
              <p className="text-gray-500 mt-1">
                Showing {Math.min(10, fileData.totalRows)} of {fileData.totalRows} rows. Review before syncing.
              </p>
            </div>

            <Card>
              <CardContent className="pt-5 p-0">
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 sticky top-0">
                        <th className="text-left py-2 px-3 font-medium text-gray-500 border-b">#</th>
                        {getMappingsForSync().map(m => (
                          <th key={m.targetField} className="text-left py-2 px-3 font-medium text-gray-700 border-b whitespace-nowrap">
                            {getFieldByKey(entity, m.targetField)?.label || m.targetField}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {fileData.rows.slice(0, 10).map((row, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-1.5 px-3 text-gray-400 text-xs">{i + 1}</td>
                          {getMappingsForSync().map(m => (
                            <td key={m.targetField} className="py-1.5 px-3 text-gray-900 max-w-[180px] truncate text-xs">
                              {String(row[m.sourceHeader] || '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep('map')}>← Back to Mapping</Button>
              <Button onClick={handleSync} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-lg px-6">
                <CloudArrowUpIcon className="w-5 h-5" />
                Sync {fileData.totalRows} {entity}
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 6: SYNCING ═══ */}
        {step === 'import' && (
          <div className="text-center py-16">
            <ArrowPathIcon className="w-16 h-16 animate-spin text-blue-500 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-900">Syncing your data...</h2>
            <p className="text-gray-500 mt-2">
              Processing {fileData?.totalRows} {entity}. This may take a moment.
            </p>
          </div>
        )}

        {/* ═══ STEP 7: DONE ═══ */}
        {step === 'done' && syncResult && (
          <div className="space-y-8">
            {/* Success header */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircleIcon className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Import Complete!</h2>
              <p className="text-gray-500 mt-1">
                {generateSummaryText(entity, syncResult.received, syncResult.created, syncResult.updated, syncResult.skipped, syncResult.errors)}
              </p>
            </div>

            {/* Results */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Received', value: syncResult.received, color: 'gray' },
                { label: 'Created', value: syncResult.created, color: 'green' },
                { label: 'Updated', value: syncResult.updated, color: 'blue' },
                { label: 'Skipped', value: syncResult.skipped, color: 'amber' },
              ].map(r => (
                <div key={r.label} className={`bg-${r.color}-50 rounded-xl p-4 text-center`}>
                  <div className={`text-2xl font-bold text-${r.color}-700`}>{r.value}</div>
                  <div className={`text-xs text-${r.color}-600`}>{r.label}</div>
                </div>
              ))}
            </div>

            {/* Storefront link */}
            <Card className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
              <CardContent className="pt-6 text-center">
                <GlobeAltIcon className="w-10 h-10 mx-auto mb-3 text-green-200" />
                <h3 className="text-xl font-bold">Your storefront is live!</h3>
                <p className="text-green-100 mt-1">
                  {tenant?.subdomain ? (
                    <>Visit <strong>{tenant.subdomain}.retailsync.africa</strong> to see your online store.</>
                  ) : (
                    <>Your products are synced. Configure your storefront in the tenant admin panel.</>
                  )}
                </p>
                <div className="flex gap-3 justify-center mt-4">
                  {tenant?.subdomain && (
                    <a
                      href={`https://${tenant.subdomain}.retailsync.africa`}
                      target="_blank"
                      rel="noopener"
                      className="px-6 py-2.5 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 transition-colors inline-flex items-center gap-2"
                    >
                      <GlobeAltIcon className="w-4 h-4" />
                      Visit Storefront
                    </a>
                  )}
                  <a
                    href="https://app.retailsync.africa"
                    target="_blank"
                    rel="noopener"
                    className="px-6 py-2.5 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20"
                  >
                    Open Admin Panel
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Next steps */}
            <Card>
              <CardHeader>
                <CardTitle>What's Next?</CardTitle>
                <CardDescription>Continue setting up your RetailsyncGo store</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { icon: ArrowUpTrayIcon, label: 'Import More', desc: 'Add customers, inventory, or transactions', action: reset },
                    { icon: ShoppingBagIcon, label: 'Configure Store', desc: 'Customize your storefront branding & settings', href: 'https://app.retailsync.africa' },
                    { icon: PuzzlePieceIcon, label: 'Connect Providers', desc: 'Install Square, Shopify, or other integrations', href: '#/marketplace' },
                  ].map(item => (
                    item.action ? (
                      <button key={item.label} onClick={item.action}
                        className="text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                        <item.icon className="w-5 h-5 text-blue-600 mb-2" />
                        <h4 className="font-medium text-sm text-gray-900">{item.label}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                      </button>
                    ) : (
                      <a key={item.label} href={item.href} target="_blank" rel="noopener"
                        className="text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors block">
                        <item.icon className="w-5 h-5 text-blue-600 mb-2" />
                        <h4 className="font-medium text-sm text-gray-900">{item.label}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                      </a>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* RetailsyncGo POS upsell */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white text-center">
              <CpuChipIcon className="w-10 h-10 mx-auto mb-3 text-blue-200" />
              <h3 className="text-lg font-bold">Tired of manual imports?</h3>
              <p className="text-blue-100 text-sm mt-1 max-w-md mx-auto">
                Download <strong>RetailsyncGo POS</strong> — our Electron desktop app. Auto-syncs inventory and sales at startup. Works offline. Free.
              </p>
              <a href="https://retailsync.africa" target="_blank" rel="noopener"
                className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 bg-white text-blue-700 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                <CloudArrowUpIcon className="w-4 h-4" />
                Get RetailsyncGo POS
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
